import React, {
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useState,
    useTransition,
} from "react";
import { Button, Form, Modal, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs-timezone-iana-plugin";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    deleteTransaction,
    getTransactions,
    updateTransaction,
    updateTransactionCategory,
    fetchCategoryRules,
    deleteCategoryRule,
} from "../api/transactions";
import {
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory,
} from "../api/categories";
import { invalidateCategoriesCache, fetchCategoriesCached } from "../helpers/categoryApiCache";
import ManualTransactionModal from "../components/ManualTransactionModal";
import { DeleteTransactionModal, EditTransactionModal } from "../components/TransactionModals";
import TxRuleModal from "../components/TxRuleModal";
import CategoryEditModal from "../components/CategoryEditModal";
import CategorySelectGrouped from "../components/transactions/CategorySelectGrouped.jsx";
import Loader from "../components/common/Loader";
import CardLabel from "../helpers/CardLabel";
import CounterTags from "../components/CounterTags";
import {
    buildTransactionTableSearchHaystack,
    formatDateToKyiv,
    isRealIncome,
    isUncategorized,
} from "../helpers/utils";
import { useFopLabels } from "../hooks/useFopLabels";
import { TIMEZONE } from "../constans";
import { Pencil, Trash2 } from "lucide-react";
import { compareNameUkStrings, sortObjectsByNameUk } from "../helpers/sortUk";

dayjs.extend(utc);
dayjs.extend(timezone);

const TABS = [
    { id: "overview", path: "overview", label: "Огляд" },
    { id: "ledger", path: "journal", label: "Журнал" },
    { id: "rules", path: "rules", label: "Правила" },
    { id: "categories", path: "categories", label: "Категорії" },
];

const TAB_BY_PATH = TABS.reduce((acc, tab) => {
    acc[tab.path] = tab;
    acc[tab.id] = tab;
    return acc;
}, {});

const LEDGER_SCOPE_BASE = [
    { id: "all", label: "Усі" },
    { id: "cash", label: "Готівка" },
    { id: "business", label: "Бізнес" },
    { id: "personal", label: "Особисті" },
];

function applyLedgerFilters(list, baseScope, uncatOnly, showIncome, showExpense, hideInternal) {
    let out = list;
    if (baseScope === "cash") out = out.filter((tx) => tx.paymentType === "cash");
    else if (baseScope === "business") out = out.filter((tx) => tx.categoryType === "business");
    else if (baseScope === "personal") out = out.filter((tx) => tx.categoryType === "personal");

    if (hideInternal) out = out.filter((tx) => !tx.isInternal && tx.category !== "Внутрішні перекази");

    if (uncatOnly) out = out.filter((tx) => isUncategorized(tx));

    const bothDirs = showIncome && showExpense;
    if (!bothDirs) {
        out = out.filter((tx) => {
            const d = tx.direction || "";
            if (d === "income") return showIncome;
            if (d === "expense") return showExpense;
            return true;
        });
    }
    return out;
}

/**
 * Плоський список рядків для відображення дерева категорій у консолі (батьки → діти).
 * «Зламані» посилання на батька без документа в масиві — окремий рядок із прапорцем.
 */
function flattenCategoryRowsForConsole(categories) {
    if (!Array.isArray(categories) || !categories.length) return [];

    const cmp = (a, b) =>
        (a.order ?? 1000) - (b.order ?? 1000) || compareNameUkStrings(a.name, b.name);

    const idSet = new Set(categories.map((c) => String(c._id)));
    const byParent = new Map();
    const rootKey = "__root__";
    for (const c of categories) {
        const pk = c.parentId == null || c.parentId === "" ? rootKey : String(c.parentId);
        if (!byParent.has(pk)) byParent.set(pk, []);
        byParent.get(pk).push(c);
    }
    for (const list of byParent.values()) list.sort(cmp);

    const out = [];
    const seen = new Set();

    function visitSubtree(node, depth, opts = {}) {
        const sid = String(node._id);
        if (seen.has(sid)) return;
        seen.add(sid);
        const { brokenParent } = opts;
        out.push({ node, depth, ...(brokenParent ? { brokenParent: true } : {}) });
        for (const ch of byParent.get(sid) || []) visitSubtree(ch, depth + 1, {});
    }

    for (const root of byParent.get(rootKey) || []) visitSubtree(root, 0, {});

    while (seen.size < categories.length) {
        const rem = categories.filter((c) => !seen.has(String(c._id)));
        const remIds = new Set(rem.map((c) => String(c._id)));
        const head =
            rem.find((c) => {
                const pk =
                    c.parentId == null || c.parentId === "" ? rootKey : String(c.parentId);
                return !remIds.has(pk);
            }) ?? rem[0];
        const pk =
            head.parentId == null || head.parentId === "" ? "" : String(head.parentId);
        const missingParent = Boolean(pk) && !idSet.has(pk);
        visitSubtree(head, 0, { brokenParent: missingParent });
    }

    return out;
}

function ExpenseCategorySummaryLink({
    category,
    total,
    count,
    selectedMonth,
    turnover,
    getExpensePercent,
}) {
    return (
        <Link
            to={`/transactions/business?category=${encodeURIComponent(category)}&month=${encodeURIComponent(selectedMonth)}`}
            className="text-decoration-none text-reset d-block"
            title={`Усі операції: ${category} (${selectedMonth})`}
        >
            <div className="d-flex justify-content-between align-items-center px-3 py-2 tx-console-top-expense-hit">
                <span className="small">
                    {category} <span className="text-muted">({count})</span>
                </span>
                <span className="fw-semibold">
                    {Math.round(Math.abs(total / 100)).toLocaleString("uk-UA")} грн
                    {turnover > 0 && (
                        <span className="text-muted small ms-1">({getExpensePercent(total)}%)</span>
                    )}
                </span>
            </div>
        </Link>
    );
}

function formatTxDescription(raw, maxLen = 38) {
    if (!raw) return "";
    let s = raw.replace(/^від:\s*/i, "").replace(/^Фізична особа-підприємець\s*/i, "ФОП ");
    if (s.length > maxLen) s = s.slice(0, maxLen) + "…";
    return s;
}

/**
 * Адмінська консоль транзакцій — окремий UX від /transactions, з акцентом на мобільність і швидкі дії.
 */
export default function AdminTransactionsConsolePage({ user }) {
    const isAdmin = user?.role === "admin";
    const isFinance = user?.role === "finance";
    const { labelBySlug } = useFopLabels();
    const { tab: tabPath } = useParams();

    const defaultTab = isFinance ? "ledger" : "overview";
    const activeTab = TAB_BY_PATH[tabPath]?.id || defaultTab;
    const [selectedMonth, setSelectedMonth] = useState(
        () => localStorage.getItem("selectedMonth") || dayjs().format("YYYY-MM")
    );
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cashBalance, setCashBalance] = useState(0);

    const [search, setSearch] = useState("");
    const deferredSearch = useDeferredValue(search);
    const [ledgerScope, setLedgerScope] = useState("all");
    /** Незалежний від базового каналу — можна ввімкнути разом з «Бізнес» або «Особисті». */
    const [ledgerUncatOnly, setLedgerUncatOnly] = useState(false);
    const [ledgerShowIncome, setLedgerShowIncome] = useState(false);
    const [ledgerShowExpense, setLedgerShowExpense] = useState(true);
    const [ledgerHideInternal, setLedgerHideInternal] = useState(true);
    const [isLedgerTransitionPending, startLedgerTransition] = useTransition();
    const [sortConfig, setSortConfig] = useState({ key: "time", direction: "desc" });

    const [showManual, setShowManual] = useState(false);
    const [editTx, setEditTx] = useState(null);
    const [showRuleModal, setShowRuleModal] = useState(false);
    const [editRule, setEditRule] = useState(null);
    const [rules, setRules] = useState([]);
    const [rulesLoading, setRulesLoading] = useState(false);
    const [deleteRuleId, setDeleteRuleId] = useState(null);

    const [deleteId, setDeleteId] = useState(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);

    const [catType, setCatType] = useState("business");
    const safeCatType = isFinance && catType === "personal" ? "business" : catType;
    const [catList, setCatList] = useState([]);
    const [editCategory, setEditCategory] = useState(null);
    const [showCatEdit, setShowCatEdit] = useState(false);

    const monthFrom = dayjs(selectedMonth).startOf("month");
    const monthTo = dayjs(selectedMonth).endOf("month");
    const unixFrom = monthFrom.unix();
    const unixTo = monthTo.unix();

    useEffect(() => {
        localStorage.setItem("selectedMonth", selectedMonth);
    }, [selectedMonth]);

    /** Журнал: завантажити словник категорій у кеш заздалегідь (3 легкі запити замість хвості при скролі). */
    useEffect(() => {
        if (activeTab !== "ledger") return;
        fetchCategoriesCached("business");
        fetchCategoriesCached("personal");
        fetchCategoriesCached("cash");
    }, [activeTab]);

    const loadTransactionsByMonth = useCallback(async (month) => {
        setLoading(true);
        try {
            const start = dayjs(month).tz(TIMEZONE).startOf("month").toISOString();
            const end = dayjs(month).tz(TIMEZONE).endOf("month").toISOString();
            const res = await getTransactions(start, end);
            setTransactions(res);
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }, []);

    const loadCashBalance = useCallback(async (month) => {
        try {
            const end = dayjs(month).tz(TIMEZONE).endOf("month").toISOString();
            const res = await fetch(
                `/api/transactions/cash-balance-summary?dateTo=${encodeURIComponent(end)}`,
                { credentials: "include" }
            );
            if (!res.ok) throw new Error("cash balance");
            const data = await res.json();
            setCashBalance((data.balance ?? 0) / 100);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        loadTransactionsByMonth(selectedMonth);
        loadCashBalance(selectedMonth);
    }, [selectedMonth, loadTransactionsByMonth, loadCashBalance]);

    useEffect(() => {
        if (activeTab !== "rules") return;
        (async () => {
            setRulesLoading(true);
            try {
                const list = await fetchCategoryRules();
                setRules(Array.isArray(list) ? list : []);
            } catch (e) {
                console.error(e);
                setRules([]);
            }
            setRulesLoading(false);
        })();
    }, [activeTab]);

    useEffect(() => {
        if (activeTab !== "categories") return;
        (async () => {
            try {
                const data = await getCategories(safeCatType);
                setCatList(Array.isArray(data) ? data : []);
            } catch {
                setCatList([]);
            }
        })();
    }, [activeTab, safeCatType]);

    const handleManualSubmit = (tx) => {
        setTransactions((prev) => [{ ...tx, _id: Date.now().toString() }, ...prev]);
        setShowManual(false);
        loadTransactionsByMonth(selectedMonth);
        loadCashBalance(selectedMonth);
    };

    const handleImportAll = async () => {
        try {
            const [mono, nova] = await Promise.all([
                fetch(`/api/transactions/import/monobank?from=${unixFrom}&to=${unixTo}`).then((r) => {
                    if (!r.ok) throw new Error("mono");
                    return r.json();
                }),
                fetch(`/api/transactions/import/novapay?dateFrom=${unixFrom}&dateTo=${unixTo}`).then((r) => {
                    if (!r.ok) throw new Error("nova");
                    return r.json();
                }),
            ]);
            alert(
                `Імпорт завершено:\nMonobank: ${mono?.imported ?? "OK"}\nNovaPay: ${nova?.count ?? "OK"}`
            );
            await loadTransactionsByMonth(selectedMonth);
            await loadCashBalance(selectedMonth);
        } catch {
            alert("Помилка імпорту");
        }
    };

    const handleRecategorizeMonth = async () => {
        const from = dayjs(selectedMonth).startOf("month").toISOString();
        const to = dayjs(selectedMonth).endOf("month").toISOString();
        await fetch("/api/category-rules/recategorize", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ from, to }),
        });
        await loadTransactionsByMonth(selectedMonth);
    };

    const summarize = (txs, type) => {
        const income = txs
            .filter(
                (tx) =>
                    tx.categoryType === type &&
                    isRealIncome(tx) &&
                    tx.category
            )
            .reduce((sum, tx) => sum + tx.amount, 0);

        const expense = txs
            .filter(
                (tx) =>
                    tx.categoryType === type &&
                    tx.direction === "expense" &&
                    !tx.excludeFromTotals
            )
            .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);

        return { income, expense, balance: income - expense };
    };

    function calculateCashSummary(txs) {
        let income = 0;
        let incomeCount = 0;
        let expense = 0;

        txs.forEach((tx) => {
            if (tx.paymentType !== "cash") return;
            if (!tx.category) return;
            if (tx.direction === "income") {
                income += tx.amount;
                incomeCount += 1;
            } else if (tx.direction === "expense") {
                expense += Math.abs(tx.amount);
            }
        });

        return {
            income: income / 100,
            incomeCount,
            expense: expense / 100,
            balance: (income - expense) / 100,
        };
    }

    const getCategorySummaryByType = (txs, type) =>
        txs
            .filter(
                (tx) =>
                    tx.categoryType === type &&
                    !(tx.direction === "income" && tx.isCashMirror)
            )
            .reduce((acc, tx) => {
                const cat = tx.category || "Без категорії";
                if (!acc[cat]) acc[cat] = { total: 0, count: 0 };
                acc[cat].total += Math.abs(tx.amount);
                acc[cat].count += 1;
                return acc;
            }, {});

    const summaryBusiness = summarize(transactions, "business");
    const summaryPersonal = summarize(transactions, "personal");
    const summaryCash = calculateCashSummary(transactions);
    const turnover = summaryBusiness.income;

    const financeVisibleTransactions = useMemo(
        () => isFinance
            ? transactions.filter(tx =>
                tx.categoryType !== "personal" &&
                tx.category !== "Дивіденди" &&
                tx.category !== "ЗП" &&
                (tx.paymentType !== "cash" || ledgerScope === "cash")
            )
            : transactions,
        [transactions, isFinance, ledgerScope]
    );

    const scoped = useMemo(
        () =>
            applyLedgerFilters(
                financeVisibleTransactions,
                ledgerScope,
                ledgerUncatOnly,
                ledgerShowIncome,
                ledgerShowExpense,
                ledgerHideInternal
            ),
        [
            financeVisibleTransactions,
            ledgerScope,
            ledgerUncatOnly,
            ledgerShowIncome,
            ledgerShowExpense,
            ledgerHideInternal,
        ]
    );

    const searched = useMemo(() => {
        const tokens = deferredSearch
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);
        if (!tokens.length) return scoped;

        return scoped.filter((tx) => {
            const description = (tx.description || "").toLowerCase();
            const comment = (tx.comment || "").toLowerCase();
            const amount = Math.abs(tx.amount / 100).toString();
            const extra = buildTransactionTableSearchHaystack(tx, labelBySlug);
            const haystack = `${description} ${comment} ${amount} ${extra}`;
            return tokens.every((t) => haystack.includes(t));
        });
    }, [scoped, deferredSearch, labelBySlug]);

    const sortedLedger = useMemo(() => {
        return [...searched].sort((a, b) => {
            const key = sortConfig.key;
            let aValue = a[key];
            let bValue = b[key];
            if (key === "time") {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            } else {
                aValue = (aValue ?? "").toString().toLowerCase();
                bValue = (bValue ?? "").toString().toLowerCase();
            }
            if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [searched, sortConfig]);

    /** Дохід / витрати по видимих рядках (копійки → грн); витрати — сума модулів від'ємних `amount`. */
    const ledgerDisplayedIncomeExpenseGrn = useMemo(() => {
        let incomeMinor = 0;
        let expenseMinor = 0;
        for (const tx of sortedLedger) {
            const a = Number(tx.amount ?? 0);
            if (a > 0) incomeMinor += a;
            else if (a < 0) expenseMinor += a;
        }
        return {
            income: incomeMinor / 100,
            expenseAbs: Math.abs(expenseMinor) / 100,
        };
    }, [sortedLedger]);

    const rulesAlphabetical = useMemo(
        () => sortObjectsByNameUk(rules),
        [rules]
    );

    const categoryConsoleTreeRows = useMemo(
        () => flattenCategoryRowsForConsole(
            isFinance ? catList.filter(c => c.type !== "personal") : catList
        ),
        [catList, isFinance]
    );

    const handleSort = (key) => {
        startLedgerTransition(() => {
            setSortConfig((prev) => ({
                key,
                direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
            }));
        });
    };

    const handleCommentBlur = async (txId, comment) => {
        await updateTransactionCategory(txId, { comment });
        setTransactions((prev) =>
            prev.map((tx) =>
                tx._id === txId ? { ...tx, comment, userEditedComment: true } : tx
            )
        );
    };

    const handleTypeChange = async (tx, newType) => {
        await updateTransactionCategory(tx._id, {
            categoryType: newType,
            direction: tx.direction,
            isManual: true,
        });
        const updated = { ...tx, categoryType: newType, isManual: true };
        setTransactions((prev) => prev.map((t) => (t._id === tx._id ? updated : t)));
    };

    const handleCategoryChange = async (tx, category) => {
        const res = await updateTransactionCategory(tx._id, {
            categoryType: tx.categoryType,
            category,
            isManual: true,
        });
        const updated = { ...tx, category, isManual: true };
        /** Автоправило на сервері розкидало ту ж категорію на такі самі транзакції. */
        const autoApplied = new Set(res?.autoRule?.appliedIds || []);
        setTransactions((prev) =>
            prev.map((t) => {
                if (t._id === tx._id) return updated;
                if (autoApplied.has(t._id)) return { ...t, category };
                return t;
            })
        );
    };

    const getExpensePercent = (amountMinor) => {
        if (!turnover) return 0;
        return ((Math.abs(amountMinor) / turnover) * 100).toFixed(1);
    };

    const businessCategoriesSummary = getCategorySummaryByType(transactions, "business");

    const businessOverviewExpenseCategoriesSplit = useMemo(() => {
        const rows = Object.entries(businessCategoriesSummary)
            .filter(([c]) => c !== "Надходження")
            .filter(([c]) => c !== "Внутрішні перекази")
            .sort((a, b) => Math.abs(b[1].total) - Math.abs(a[1].total));
        const mid = Math.ceil(rows.length / 2);
        return [rows.slice(0, mid), rows.slice(mid)];
    }, [businessCategoriesSummary]);

    /**
     * Довідник категорій для рядка/форми. Довідник "cash" містить і бізнес-, і особисті категорії
     * (Кафе, Одяг, Подорожі…), тому фінансист (finance) для готівки бачить лише бізнесовий довідник — так само, як у формі створення.
     */
    const categoryApiType = (paymentType, categoryType) =>
        paymentType === "cash" && !isFinance ? "cash" : categoryType;

    const subcategoryField = ({ transaction, onChange, form, setForm }) => {
        if (transaction) {
            const apiType = categoryApiType(transaction.paymentType, transaction.categoryType);
            return (
                <CategorySelectGrouped
                    apiType={apiType}
                    value={transaction.category || ""}
                    onChange={(cat) => onChange({ ...transaction, category: cat })}
                    className="form-select"
                />
            );
        }
        if (form && setForm) {
            const apiType = categoryApiType(form.paymentType, form.categoryType);
            return (
                <CategorySelectGrouped
                    apiType={apiType}
                    value={form.category || ""}
                    onChange={(cat) => setForm((p) => ({ ...p, category: cat }))}
                    className="form-select"
                />
            );
        }
        return null;
    };

    const handleCatSave = async (data) => {
        if (editCategory) {
            await updateCategory(editCategory._id, data);
        } else {
            await createCategory(data);
        }
        setEditCategory(null);
        setShowCatEdit(false);
        invalidateCategoriesCache(safeCatType);
        const fresh = await getCategories(safeCatType);
        setCatList(Array.isArray(fresh) ? fresh : []);
    };

    const handleCatDelete = async (cat) => {
        if (!window.confirm(`Вимкнути категорію "${cat.name}"?`)) return;
        await deleteCategory(cat._id);
        invalidateCategoriesCache(safeCatType);
        const fresh = await getCategories(safeCatType);
        setCatList(Array.isArray(fresh) ? fresh : []);
    };

    if (!isAdmin && !isFinance) {
        return (
            <div className="p-4">
                <p>Ця сторінка доступна лише адміністраторам.</p>
                <Link to="/transactions">Назад до транзакцій</Link>
            </div>
        );
    }

    return (
        <div className="tx-console">
            <header className="tx-console-hero">
                <p className="tx-console-hero__title">Транзакції</p>
                <div className="tx-console-hero__row">
                    <div className="d-flex flex-wrap align-items-end gap-2">
                        <div>
                            <div className="small text-white-50 mb-1">Місяць</div>
                            <DatePicker
                                selected={dayjs(selectedMonth).toDate()}
                                onChange={(date) => {
                                    setSelectedMonth(dayjs(date).format("YYYY-MM"));
                                }}
                                showMonthYearPicker
                                dateFormat="LLLL yyyy"
                                locale="uk"
                                className="form-control"
                            />
                        </div>
                        {!isFinance && (
                        <>
                        <Button variant="light" className="text-nowrap" onClick={handleImportAll}>
                            Імпорт Mono + Nova
                        </Button>
                        <Button variant="outline-light" className="text-nowrap" onClick={handleRecategorizeMonth}>
                            Перекатегоризувати місяць
                        </Button>
                        </>
                        )}
                    </div>
                </div>
                {activeTab === "ledger" && (
                    <div className="tx-console-hero__ledger-summary" aria-live="polite">
                        {loading ? (
                            <span className="text-white-50 small">Завантаження…</span>
                        ) : (
                            <span className="text-white-50 small">
                                Показано:{" "}
                                <strong className="text-white">
                                    {sortedLedger.length.toLocaleString("uk-UA")}
                                </strong>
                                {ledgerShowIncome && <>
                                    {" · "}Дохід:{" "}
                                    <strong className="text-white">
                                        {Math.round(ledgerDisplayedIncomeExpenseGrn.income).toLocaleString("uk-UA")}
                                    </strong>
                                </>}
                                {ledgerShowExpense && <>
                                    {" · "}Витрати:{" "}
                                    <strong className="text-white">
                                        {Math.round(ledgerDisplayedIncomeExpenseGrn.expenseAbs).toLocaleString("uk-UA")}
                                    </strong>
                                </>}
                            </span>
                        )}
                    </div>
                )}
            </header>

            <div className="tx-console-sticky-tabs">
                <div className="tx-console-tabs-bar">
                    <div className="tx-console-tabs" role="tablist">
                        {TABS.map((t) => (
                            <Link
                                key={t.id}
                                to={`/transactions/${t.path}`}
                                role="tab"
                                aria-selected={activeTab === t.id}
                                className={`tx-console-tab ${activeTab === t.id ? "active" : ""}`}
                            >
                                {t.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>

            {activeTab === "overview" && (
                <section className="tx-console-panel">
                    {loading ? (
                        <Loader />
                    ) : (
                        <>
                            <div className="tx-console-overview-grid mb-4">
                                {!isFinance && (
                                <Link to="/transactions/cash" className="text-decoration-none text-reset">
                                    <article className="tx-console-stat-card">
                                        <h4>Готівка</h4>
                                        <div className="tx-console-stat-card__big">
                                            {Math.round(cashBalance).toLocaleString("uk-UA")} грн
                                        </div>
                                        <div className="tx-console-stat-card__meta">
                                            Прибуток періоду ≈ {Math.round(summaryCash.balance).toLocaleString("uk-UA")}{" "}
                                            грн · надходжень {summaryCash.incomeCount}
                                        </div>
                                    </article>
                                </Link>
                                )}
                                {!isFinance && (
                                <Link to="/transactions/business" className="text-decoration-none text-reset">
                                    <article className="tx-console-stat-card">
                                        <h4>Бізнес</h4>
                                        <div className="tx-console-stat-card__big">
                                            {Math.round(summaryBusiness.balance / 100).toLocaleString("uk-UA")} грн
                                        </div>
                                        <div className="tx-console-stat-card__meta">
                                            Доходи {Math.round(summaryBusiness.income / 100).toLocaleString("uk-UA")} ·
                                            витрати {Math.round(summaryBusiness.expense / 100).toLocaleString("uk-UA")}
                                        </div>
                                    </article>
                                </Link>
                                )}
                                {!isFinance && (
                                <Link to="/transactions/personal" className="text-decoration-none text-reset">
                                    <article className="tx-console-stat-card">
                                        <h4>Особисті</h4>
                                        <div className="tx-console-stat-card__big">
                                            {Math.round(summaryPersonal.balance / 100).toLocaleString("uk-UA")} грн
                                        </div>
                                        <div className="tx-console-stat-card__meta">
                                            Доходи {Math.round(summaryPersonal.income / 100).toLocaleString("uk-UA")} ·
                                            витрати {Math.round(summaryPersonal.expense / 100).toLocaleString("uk-UA")}
                                        </div>
                                    </article>
                                </Link>
                                )}
                            </div>
                            <h5 className="small text-uppercase text-muted fw-bold mb-3">
                                Витрати бізнесу
                            </h5>
                            <div className="row g-2">
                                <div className="col-md-6 d-flex flex-column gap-2">
                                    {businessOverviewExpenseCategoriesSplit[0]
                                        .filter(([c]) => !isFinance || (c !== "Дивіденди" && c !== "ЗП"))
                                        .map(
                                        ([category, { total, count }]) => (
                                            <ExpenseCategorySummaryLink
                                                key={category}
                                                category={category}
                                                total={total}
                                                count={count}
                                                selectedMonth={selectedMonth}
                                                turnover={turnover}
                                                getExpensePercent={getExpensePercent}
                                            />
                                        )
                                    )}
                                </div>
                                <div className="col-md-6 d-flex flex-column gap-2">
                                    {businessOverviewExpenseCategoriesSplit[1]
                                        .filter(([c]) => !isFinance || (c !== "Дивіденди" && c !== "ЗП"))
                                        .map(
                                        ([category, { total, count }]) => (
                                            <ExpenseCategorySummaryLink
                                                key={category}
                                                category={category}
                                                total={total}
                                                count={count}
                                                selectedMonth={selectedMonth}
                                                turnover={turnover}
                                                getExpensePercent={getExpensePercent}
                                            />
                                        )
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </section>
            )}

            {activeTab === "ledger" && (
                <section className="tx-console-panel">
                    <div className="tx-console-toolbar">
                        <div className="d-flex gap-2 align-items-center">
                            <Form.Control
                                type="search"
                                placeholder="Пошук: слова через пробіл — усі збіги (опис, сума, контрагенти…)"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <Button variant="primary" className="px-3 flex-shrink-0" onClick={() => setShowManual(true)}>
                                +
                            </Button>
                        </div>
                        <div className="tx-console-toolbar-filters">
                            <div className="tx-console-chip-row">
                                {LEDGER_SCOPE_BASE.filter(f => !isFinance || f.id === "cash").map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        className={`tx-console-chip ${ledgerScope === f.id ? "active" : ""}`}
                                        onClick={() =>
                                            startLedgerTransition(() => {
                                                setLedgerScope(prev => prev === f.id ? "all" : f.id);
                                            })
                                        }
                                    >
                                        {f.label}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className={`tx-console-chip tx-console-chip--compound ${ledgerUncatOnly ? "active" : ""}`}
                                    onClick={() =>
                                        startLedgerTransition(() => {
                                            setLedgerUncatOnly((v) => !v);
                                        })
                                    }
                                >
                                    Без категорії
                                </button>
                                <button
                                    type="button"
                                    className={`tx-console-chip tx-console-chip--check ${ledgerHideInternal ? "active" : ""}`}
                                    onClick={() =>
                                        startLedgerTransition(() => {
                                            setLedgerHideInternal((v) => !v);
                                        })
                                    }
                                >
                                    {ledgerHideInternal ? "✓ " : ""}Сховати внутр. перекази
                                </button>
                                <span className="tx-console-filter-sep" aria-hidden="true" />
                                <div
                                    className="d-flex align-items-center gap-2 tx-drill-toolbar__dir-squares"
                                    role="group"
                                    aria-label="Тип операцій у таблиці"
                                >
                                    <button
                                        type="button"
                                        className={`tx-drill-dir-square tx-drill-dir-square--income${ledgerShowIncome ? " tx-drill-dir-square--on" : ""}`}
                                        aria-pressed={ledgerShowIncome}
                                        aria-label={
                                            ledgerShowIncome
                                                ? "Приховати доходи в таблиці"
                                                : "Показувати доходи в таблиці"
                                        }
                                        title={
                                            ledgerShowIncome
                                                ? "Доходи: показано — клік, щоб приховати"
                                                : "Доходи: приховано — клік, щоб показати"
                                        }
                                        onClick={() => startLedgerTransition(() => setLedgerShowIncome((v) => !v))}
                                    />
                                    <button
                                        type="button"
                                        className={`tx-drill-dir-square tx-drill-dir-square--expense${ledgerShowExpense ? " tx-drill-dir-square--on" : ""}`}
                                        aria-pressed={ledgerShowExpense}
                                        aria-label={
                                            ledgerShowExpense
                                                ? "Приховати витрати в таблиці"
                                                : "Показувати витрати в таблиці"
                                        }
                                        title={
                                            ledgerShowExpense
                                                ? "Витрати: показано — клік, щоб приховати"
                                                : "Витрати: приховано — клік, щоб показати"
                                        }
                                        onClick={() => startLedgerTransition(() => setLedgerShowExpense((v) => !v))}
                                    />
                                </div>
                                {isLedgerTransitionPending && (
                                    <div className="spinner-border tx-console-filter-spinner" role="status" aria-label="Фільтрується">
                                        <span className="visually-hidden">Завантаження…</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <Loader />
                    ) : (
                        <>
                            <div
                                className={`tx-console-table-wrap table-txs${
                                    isLedgerTransitionPending ? " tx-console-table--pending" : ""
                                }`}
                            >
                                <Table responsive className="table-transactions tx-console-ledger-table align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th onClick={() => handleSort("time")}>Дата</th>
                                            <th onClick={() => handleSort("amount")}>Сума</th>
                                            <th>Опис</th>
                                            {ledgerShowIncome && ledgerShowExpense && <th onClick={() => handleSort("direction")}>Тип</th>}
                                            <th>Категорія</th>
                                            <th>Підкатегорія</th>
                                            <th className="tx-console-th--card">Карта</th>
                                            <th>Коментар</th>
                                            <th />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedLedger.length === 0 ? (
                                            <tr>
                                                <td colSpan={ledgerShowIncome && ledgerShowExpense ? 9 : 8} className="text-center text-muted py-4">
                                                    Нічого не знайдено за фільтрами.
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedLedger.map((tx) => (
                                            <tr key={tx._id}>
                                                <td>{formatDateToKyiv(tx.time)}</td>
                                                <td>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip><pre className="mb-0 small text-start">{JSON.stringify(tx, null, 2)}</pre></Tooltip>}
                                                    >
                                                        <strong style={{ cursor: "help" }}>
                                                            {(tx.amount / 100).toFixed(2)} грн
                                                        </strong>
                                                    </OverlayTrigger>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center justify-content-between gap-2">
                                                        <span title={tx.description}>{formatTxDescription(tx.description)}</span>
                                                        <CounterTags tx={tx} />
                                                    </div>
                                                </td>
                                                {ledgerShowIncome && ledgerShowExpense && (
                                                    <td>
                                                        <span
                                                            className={`tx-dir-dot tx-dir-dot--${tx.direction}`}
                                                            title={tx.direction}
                                                        />
                                                    </td>
                                                )}
                                                <td>
                                                    <Form.Select
                                                        size="sm"
                                                        value={tx.categoryType}
                                                        className="form-select form-select-sm"
                                                        onChange={(e) => handleTypeChange(tx, e.target.value)}
                                                    >
                                                        <option value="business">Бізнес</option>
                                                        {!isFinance && <option value="personal">Особисті</option>}
                                                    </Form.Select>
                                                </td>
                                                <td className="tx-console-col--subcat">
                                                    <CategorySelectGrouped
                                                        apiType={categoryApiType(tx.paymentType, tx.categoryType)}
                                                        value={tx.category || ""}
                                                        onChange={(cat) => handleCategoryChange(tx, cat)}
                                                    />
                                                </td>
                                                <td className="tx-console-col--card align-top">
                                                    <CardLabel
                                                        fop={tx.fop}
                                                        cardType={tx.cardType}
                                                        layout="wrap"
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        size="sm"
                                                        defaultValue={tx.comment}
                                                        onBlur={(e) => handleCommentBlur(tx._id, e.target.value)}
                                                    />
                                                </td>
                                                <td className="text-end text-nowrap">
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        className="p-1 text-body text-decoration-none border-0 shadow-none"
                                                        onClick={() => setEditTx(tx)}
                                                    >
                                                        <Pencil size={18} aria-hidden />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        className="p-1 text-danger text-decoration-none border-0 shadow-none"
                                                        onClick={() => {
                                                            setDeleteId(tx._id);
                                                            setShowConfirmDelete(true);
                                                        }}
                                                    >
                                                        <Trash2 size={18} aria-hidden />
                                                    </Button>
                                                </td>
                                            </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="tx-console-tx-cards">
                                {sortedLedger.length === 0 ? (
                                    <p className="text-center text-muted py-4 mb-0">
                                        Нічого не знайдено за фільтрами.
                                    </p>
                                ) : (
                                    sortedLedger.map((tx) => (
                                    <article key={tx._id} className="tx-console-tx-card">
                                        <div className="tx-console-tx-card__top">
                                            <div>
                                                <div className="tx-console-tx-card__date">
                                                    {formatDateToKyiv(tx.time)} · {tx.direction}
                                                </div>
                                                <div className="tx-console-tx-card__desc" title={tx.description}>{formatTxDescription(tx.description)}</div>
                                            </div>
                                            <div
                                                className={
                                                    "tx-console-tx-card__amount " +
                                                    (tx.amount < 0 ? "text-danger" : "text-success")
                                                }
                                            >
                                                {(tx.amount / 100).toFixed(2)} грн
                                            </div>
                                        </div>
                                        <div className="mb-2 tx-console-card-label-wrap d-flex justify-content-end">
                                            <CardLabel
                                                fop={tx.fop}
                                                cardType={tx.cardType}
                                                layout="wrap"
                                            />
                                        </div>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small text-muted mb-1">Тип запису</Form.Label>
                                            <Form.Select
                                                value={tx.categoryType}
                                                onChange={(e) => handleTypeChange(tx, e.target.value)}
                                            >
                                                <option value="business">Бізнес</option>
                                                {!isFinance && <option value="personal">Особисті</option>}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small text-muted mb-1">Підкатегорія</Form.Label>
                                            <CategorySelectGrouped
                                                apiType={categoryApiType(tx.paymentType, tx.categoryType)}
                                                value={tx.category || ""}
                                                onChange={(cat) => handleCategoryChange(tx, cat)}
                                                className="form-select"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-2">
                                            <Form.Label className="small text-muted mb-1">Коментар</Form.Label>
                                            <Form.Control
                                                defaultValue={tx.comment}
                                                onBlur={(e) => handleCommentBlur(tx._id, e.target.value)}
                                            />
                                        </Form.Group>
                                        <div className="d-flex gap-2 justify-content-end">
                                            <Button variant="outline-primary" onClick={() => setEditTx(tx)}>
                                                Редагувати
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                onClick={() => {
                                                    setDeleteId(tx._id);
                                                    setShowConfirmDelete(true);
                                                }}
                                            >
                                                Видалити
                                            </Button>
                                        </div>
                                    </article>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </section>
            )}

            {activeTab === "rules" && (
                <section className="tx-console-panel">
                    <div className="d-flex flex-wrap justify-content-between gap-2 align-items-center mb-3">
                        <p className="mb-0 text-muted small">
                            Правила застосовуються при імпорті та перекатегоризації. Пріоритет: менше число — раніше.
                        </p>
                        <Button
                            onClick={() => {
                                setEditRule(null);
                                setShowRuleModal(true);
                            }}
                        >
                            + Нове правило
                        </Button>
                    </div>
                    {rulesLoading ? (
                        <Loader />
                    ) : (
                        rulesAlphabetical.filter(r => !isFinance || (r.result?.categoryType !== "personal" && r.result?.category !== "ЗП" && r.result?.category !== "Дивіденди")).map((r) => (
                            <div key={r._id} className="tx-console-rule-card">
                                <div className="d-flex justify-content-between align-items-center gap-2">
                                    <div className="tx-console-rule-card__title-row min-w-0 flex-grow-1">
                                        <span className="tx-console-rule-card__name">{r.name}</span>
                                        <span className="tx-console-rule-card__meta text-muted">
                                            {" ("}
                                            <svg
                                                className="tx-console-rule-card__meta-chevron"
                                                width="13"
                                                height="13"
                                                viewBox="0 0 14 14"
                                                aria-hidden="true"
                                                focusable="false"
                                            >
                                                <path
                                                    d="M8.5 2.5 3.5 7l5 4.5"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.75"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            {r.result?.categoryType}/{r.result?.category} · пріоритет{" "}
                                            {r.priority})
                                        </span>
                                    </div>
                                    <div className="d-flex gap-1 flex-shrink-0 align-items-center">
                                        <Button
                                            type="button"
                                            variant="link"
                                            size="sm"
                                            className="p-1 text-body text-decoration-none border-0 shadow-none"
                                            title="Редагувати"
                                            aria-label={`Редагувати правило ${r.name}`}
                                            onClick={() => {
                                                setEditRule(r);
                                                setShowRuleModal(true);
                                            }}
                                        >
                                            <Pencil size={18} aria-hidden />
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="link"
                                            size="sm"
                                            className="p-1 text-danger text-decoration-none border-0 shadow-none"
                                            title="Видалити"
                                            aria-label={`Видалити правило ${r.name}`}
                                            onClick={() => setDeleteRuleId(r._id)}
                                        >
                                            <Trash2 size={18} aria-hidden />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </section>
            )}

            {activeTab === "categories" && (
                <section className="tx-console-panel">
                    <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
                        <Form.Select
                            className="w-auto"
                            value={safeCatType}
                            onChange={(e) => setCatType(e.target.value)}
                        >
                            <option value="business">Бізнес</option>
                            {!isFinance && <option value="personal">Особисті</option>}
                            {!isFinance && <option value="cash">Готівка</option>}
                        </Form.Select>
                        <Button onClick={() => setShowCatEdit(true)}>Додати категорію</Button>
                    </div>
                    <div className="tx-console-categories-wrap">
                        <div className="tx-console-category-tree">
                            <div className="tx-console-category-tree__head">
                                <span>Назва</span>
                                <span className="tx-console-category-tree__head-order">Порядок</span>
                                <span className="text-end">Дії</span>
                            </div>
                            <div className="tx-console-category-tree__body" role="list">
                                {categoryConsoleTreeRows.length === 0 ? (
                                    <div className="tx-console-category-tree__empty text-muted py-4 text-center">
                                        Немає категорій для цього типу. Натисніть «Додати категорію».
                                    </div>
                                ) : (
                                    categoryConsoleTreeRows.map(
                                        ({ node: c, depth, brokenParent }) => (
                                            <div
                                                key={c._id}
                                                className={`tx-console-category-tree__row${
                                                    depth > 0
                                                        ? " tx-console-category-tree__row--child"
                                                        : ""
                                                }`}
                                                role="listitem"
                                                style={{ "--tx-cat-depth": depth }}
                                            >
                                                <div className="tx-console-category-tree__name">
                                                    {depth > 0 && (
                                                        <span
                                                            className="tx-console-category-tree__branch"
                                                            aria-hidden="true"
                                                        >
                                                            ↳
                                                        </span>
                                                    )}
                                                    <span className="tx-console-category-tree__title">
                                                        {c.name}
                                                    </span>
                                                    {brokenParent && (
                                                        <span
                                                            className="badge bg-warning text-dark ms-1 fw-normal tx-console-category-tree__badge"
                                                            title="Немає категорії з таким id у цьому типі"
                                                        >
                                                            батько не знайдений
                                                        </span>
                                                    )}
                                                    {depth === 0 && c.parentId && !brokenParent && (
                                                        <span
                                                            className="badge bg-secondary-subtle text-secondary-emphasis border ms-1 fw-normal tx-console-category-tree__badge"
                                                            title="Не досяжна з основних коренів — перевір parentId"
                                                        >
                                                            поза основним деревом
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="tx-console-category-tree__order">
                                                    <span>{c.order ?? "—"}</span>
                                                </div>
                                                <div className="tx-console-category-tree__actions">
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        size="sm"
                                                        className="p-1 text-body text-decoration-none border-0 shadow-none"
                                                        title="Редагувати"
                                                        aria-label={`Редагувати категорію ${c.name}`}
                                                        onClick={() => {
                                                            setEditCategory(c);
                                                            setShowCatEdit(true);
                                                        }}
                                                    >
                                                        <Pencil size={18} aria-hidden />
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        size="sm"
                                                        className="p-1 text-danger text-decoration-none border-0 shadow-none"
                                                        title="Видалити"
                                                        aria-label={`Видалити категорію ${c.name}`}
                                                        onClick={() => handleCatDelete(c)}
                                                    >
                                                        <Trash2 size={18} aria-hidden />
                                                    </Button>
                                                </div>
                                            </div>
                                        )
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <ManualTransactionModal
                show={showManual}
                onClose={() => setShowManual(false)}
                onSubmit={handleManualSubmit}
                user={user}
                renderSubcategorySelect={(p) => subcategoryField(p)}
            />

            <EditTransactionModal
                show={editTx}
                transaction={editTx}
                onChange={setEditTx}
                onClose={() => setEditTx(null)}
                renderSubcategorySelect={(p) => subcategoryField(p)}
                onSave={async () => {
                    try {
                        await updateTransaction(editTx._id, {
                            amount: editTx.amount,
                            description: editTx.description,
                            comment: editTx.comment,
                            categoryType: editTx.categoryType,
                            category: editTx.category,
                        });
                        setTransactions((prev) => prev.map((t) => (t._id === editTx._id ? editTx : t)));
                        setEditTx(null);
                    } catch {
                        alert("Не вдалося зберегти");
                    }
                }}
            />

            <DeleteTransactionModal
                show={showConfirmDelete}
                onClose={() => setShowConfirmDelete(false)}
                onConfirm={async () => {
                    try {
                        await deleteTransaction(deleteId);
                        setTransactions((prev) => prev.filter((t) => t._id !== deleteId));
                        setShowConfirmDelete(false);
                        await loadCashBalance(selectedMonth);
                    } catch {
                        alert("Помилка видалення");
                    }
                }}
            />

            <TxRuleModal
                user={user}
                applyPeriodMonth={selectedMonth}
                show={showRuleModal}
                editRule={editRule}
                onClose={() => {
                    setEditRule(null);
                    setShowRuleModal(false);
                }}
                onEditRuleUpdated={(rule) => setEditRule(rule)}
                onApplied={() => {
                    loadTransactionsByMonth(selectedMonth);
                    fetchCategoryRules().then((list) => setRules(Array.isArray(list) ? list : []));
                }}
            />

            <Modal show={!!deleteRuleId} onHide={() => setDeleteRuleId(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Видалити правило?</Modal.Title>
                </Modal.Header>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteRuleId(null)}>
                        Ні
                    </Button>
                    <Button
                        variant="danger"
                        onClick={async () => {
                            try {
                                await deleteCategoryRule(deleteRuleId);
                                setRules((prev) => prev.filter((r) => r._id !== deleteRuleId));
                            } catch {
                                alert("Не вдалося видалити");
                            }
                            setDeleteRuleId(null);
                        }}
                    >
                        Так, видалити
                    </Button>
                </Modal.Footer>
            </Modal>

            <CategoryEditModal
                show={showCatEdit}
                edit={editCategory}
                type={catType}
                onClose={() => {
                    setEditCategory(null);
                    setShowCatEdit(false);
                }}
                onSave={handleCatSave}
            />

            <button
                type="button"
                className="tx-console-mobile-fab"
                aria-label="Додати платіж"
                onClick={() => setShowManual(true)}
            >
                +
            </button>
        </div>
    );
}
